import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Session } from '@supabase/supabase-js'
import { isSupabaseConfigured, supabase } from '../lib/supabase'
import {
  demoBranch,
  demoCartItems,
  demoCategories,
  demoMenuItems,
  demoMessages,
  demoOrders,
  demoRequests,
  demoRestaurant,
  demoTables,
} from '../data/demo'
import type {
  Branch,
  CartItem,
  ChatMessage,
  DiningTable,
  MenuCategory,
  MenuItem,
  Order,
  OrderStatus,
  Restaurant,
  ServiceRequest,
  StaffProfile,
} from '../types'

interface GuestSessionResult {
  sessionId: string
  guestId: string
  guestName: string
  tableId: string
  tableNumber: number
}

interface LamsaContextValue {
  backendMode: 'demo' | 'supabase'
  loading: boolean
  error: string | null
  restaurant: Restaurant
  branch: Branch
  categories: MenuCategory[]
  menuItems: MenuItem[]
  tables: DiningTable[]
  orders: Order[]
  cartItems: CartItem[]
  requests: ServiceRequest[]
  messages: ChatMessage[]
  session: Session | null
  staffProfile: StaffProfile | null
  guestSession: GuestSessionResult | null
  signInStaff: (email: string, password: string) => Promise<void>
  signOutStaff: () => Promise<void>
  ensureGuestSession: (tableNumber: number, guestName: string) => Promise<GuestSessionResult>
  addToCart: (item: MenuItem, guestName: string) => Promise<void>
  changeCartQty: (cartId: string, quantity: number) => Promise<void>
  submitOrder: () => Promise<void>
  updateOrderStatus: (orderId: string, status: OrderStatus) => Promise<void>
  createServiceRequest: (type: ServiceRequest['type'], message?: string) => Promise<void>
  completeRequest: (requestId: string) => Promise<void>
  sendMessage: (body: string, senderName?: string, sessionId?: string) => Promise<void>
  updateRestaurant: (patch: Partial<Restaurant>) => Promise<void>
  updateMenuItem: (id: string, patch: Partial<MenuItem>) => Promise<void>
  addMenuItem: (categoryId: string) => Promise<MenuItem>
  openTable: (tableId: string) => Promise<void>
  closeTable: (tableId: string) => Promise<void>
  refresh: () => Promise<void>
}

const LamsaContext = createContext<LamsaContextValue | null>(null)

const localKey = 'lamsa-demo-v1'

type DemoSnapshot = {
  restaurant: Restaurant
  menuItems: MenuItem[]
  tables: DiningTable[]
  orders: Order[]
  cartItems: CartItem[]
  requests: ServiceRequest[]
  messages: ChatMessage[]
}

function getDemoSnapshot(): DemoSnapshot {
  try {
    const raw = localStorage.getItem(localKey)
    if (raw) return JSON.parse(raw) as DemoSnapshot
  } catch {
    // Fall through to pristine demo data.
  }
  return {
    restaurant: demoRestaurant,
    menuItems: demoMenuItems,
    tables: demoTables,
    orders: demoOrders,
    cartItems: demoCartItems,
    requests: demoRequests,
    messages: demoMessages,
  }
}

function moneyNum(value: unknown) {
  return Number(value ?? 0)
}

function mapRestaurant(row: Record<string, any>): Restaurant {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    tagline: row.tagline ?? '',
    description: row.description ?? '',
    logoUrl: row.logo_url ?? '',
    coverUrl: row.cover_url ?? '',
    primaryColor: row.primary_color ?? '#101419',
    accentColor: row.accent_color ?? '#8fb9b0',
    currency: row.currency ?? 'BD',
    orderingEnabled: row.ordering_enabled ?? true,
  }
}

function mapBranch(row: Record<string, any>): Branch {
  return { id: row.id, restaurantId: row.restaurant_id, name: row.name, slug: row.slug }
}

function mapCategory(row: Record<string, any>): MenuCategory {
  return { id: row.id, restaurantId: row.restaurant_id, name: row.name, sortOrder: row.sort_order, active: row.active }
}

function mapMenuItem(row: Record<string, any>): MenuItem {
  return {
    id: row.id,
    restaurantId: row.restaurant_id,
    categoryId: row.category_id,
    name: row.name,
    description: row.description ?? '',
    price: moneyNum(row.price),
    imageUrl: row.image_url ?? '',
    featured: row.featured,
    available: row.available,
    sortOrder: row.sort_order,
  }
}

function mapTable(row: Record<string, any>): DiningTable {
  return {
    id: row.id,
    branchId: row.branch_id,
    number: row.table_number,
    seats: row.seats,
    status: row.status,
    guests: row.guest_count ?? 0,
    elapsedSeconds: row.elapsed_seconds ?? 0,
    sessionId: row.active_session_id ?? undefined,
  }
}

function mapRequest(row: Record<string, any>): ServiceRequest {
  return {
    id: row.id,
    sessionId: row.session_id,
    tableId: row.table_id,
    tableNumber: row.table_number ?? 0,
    type: row.request_type,
    status: row.status,
    createdAt: row.created_at,
    message: row.message ?? undefined,
  }
}

function mapMessage(row: Record<string, any>): ChatMessage {
  return {
    id: row.id,
    sessionId: row.session_id,
    senderType: row.sender_type,
    senderName: row.sender_name,
    body: row.body,
    createdAt: row.created_at,
  }
}

export function LamsaProvider({ children }: { children: ReactNode }) {
  const initial = useMemo(() => (typeof window !== 'undefined' ? getDemoSnapshot() : null), [])
  const [loading, setLoading] = useState(isSupabaseConfigured)
  const [error, setError] = useState<string | null>(null)
  const [restaurant, setRestaurant] = useState(initial?.restaurant ?? demoRestaurant)
  const [branch, setBranch] = useState<Branch>(demoBranch)
  const [categories, setCategories] = useState<MenuCategory[]>(demoCategories)
  const [menuItems, setMenuItems] = useState<MenuItem[]>(initial?.menuItems ?? demoMenuItems)
  const [tables, setTables] = useState<DiningTable[]>(initial?.tables ?? demoTables)
  const [orders, setOrders] = useState<Order[]>(initial?.orders ?? demoOrders)
  const [cartItems, setCartItems] = useState<CartItem[]>(initial?.cartItems ?? demoCartItems)
  const [requests, setRequests] = useState<ServiceRequest[]>(initial?.requests ?? demoRequests)
  const [messages, setMessages] = useState<ChatMessage[]>(initial?.messages ?? demoMessages)
  const [session, setSession] = useState<Session | null>(null)
  const [staffProfile, setStaffProfile] = useState<StaffProfile | null>(null)
  const [guestSession, setGuestSession] = useState<GuestSessionResult | null>(null)

  const persistDemo = useCallback((patch: Partial<DemoSnapshot>) => {
    if (isSupabaseConfigured) return
    const current = getDemoSnapshot()
    localStorage.setItem(localKey, JSON.stringify({ ...current, ...patch }))
  }, [])

  const loadStaffProfile = useCallback(async (activeSession: Session | null) => {
    if (!supabase || !activeSession || activeSession.user.is_anonymous) {
      setStaffProfile(null)
      return null
    }
    const { data, error: staffError } = await supabase
      .from('restaurant_staff')
      .select('id, restaurant_id, branch_id, display_name, role')
      .eq('user_id', activeSession.user.id)
      .eq('active', true)
      .maybeSingle()
    if (staffError) throw staffError
    if (!data) {
      setStaffProfile(null)
      return null
    }
    const profile: StaffProfile = {
      id: data.id,
      restaurantId: data.restaurant_id,
      branchId: data.branch_id,
      displayName: data.display_name,
      role: data.role,
    }
    setStaffProfile(profile)
    return profile
  }, [])

  const loadSupabase = useCallback(async () => {
    if (!supabase) return
    setLoading(true)
    setError(null)
    try {
      const restaurantSlug = import.meta.env.VITE_RESTAURANT_SLUG || 'ava-xin-delight'
      const branchSlug = import.meta.env.VITE_BRANCH_SLUG || 'seef-demo'
      const [{ data: authData }, restaurantResult] = await Promise.all([
        supabase.auth.getSession(),
        supabase.from('restaurants').select('*').eq('slug', restaurantSlug).single(),
      ])
      if (restaurantResult.error) throw restaurantResult.error
      const mappedRestaurant = mapRestaurant(restaurantResult.data)
      setRestaurant(mappedRestaurant)
      setSession(authData.session)

      const branchResult = await supabase
        .from('branches')
        .select('*')
        .eq('restaurant_id', mappedRestaurant.id)
        .eq('slug', branchSlug)
        .single()
      if (branchResult.error) throw branchResult.error
      const mappedBranch = mapBranch(branchResult.data)
      setBranch(mappedBranch)

      const [categoryResult, itemResult] = await Promise.all([
        supabase.from('menu_categories').select('*').eq('restaurant_id', mappedRestaurant.id).order('sort_order'),
        supabase.from('menu_items').select('*').eq('restaurant_id', mappedRestaurant.id).order('sort_order'),
      ])
      if (categoryResult.error) throw categoryResult.error
      if (itemResult.error) throw itemResult.error
      setCategories((categoryResult.data ?? []).map(mapCategory))
      setMenuItems((itemResult.data ?? []).map(mapMenuItem))

      const profile = await loadStaffProfile(authData.session)
      if (profile) {
        const [tableResult, orderResult, requestResult, messageResult] = await Promise.all([
          supabase.from('table_live_view').select('*').eq('branch_id', mappedBranch.id).order('table_number'),
          supabase.from('orders').select('*, order_items(*)').eq('branch_id', mappedBranch.id).order('created_at', { ascending: false }).limit(100),
          supabase.from('service_request_live_view').select('*').eq('branch_id', mappedBranch.id).order('created_at', { ascending: false }),
          supabase.from('chat_messages').select('*').eq('branch_id', mappedBranch.id).order('created_at', { ascending: true }).limit(200),
        ])
        if (tableResult.error) throw tableResult.error
        if (orderResult.error) throw orderResult.error
        if (requestResult.error) throw requestResult.error
        if (messageResult.error) throw messageResult.error
        setTables((tableResult.data ?? []).map(mapTable))
        setOrders((orderResult.data ?? []).map((row: any) => ({
          id: row.id,
          restaurantId: row.restaurant_id,
          branchId: row.branch_id,
          tableId: row.table_id,
          tableNumber: row.table_number,
          sessionId: row.session_id,
          status: row.status,
          createdAt: row.created_at,
          acceptedAt: row.accepted_at ?? undefined,
          preparingAt: row.preparing_at ?? undefined,
          readyAt: row.ready_at ?? undefined,
          servedAt: row.served_at ?? undefined,
          notes: row.notes ?? undefined,
          items: (row.order_items ?? []).map((item: any) => ({
            id: item.id,
            orderId: item.order_id,
            guestName: item.guest_name,
            name: item.item_name,
            quantity: item.quantity,
            unitPrice: moneyNum(item.unit_price),
          })),
        })))
        setRequests((requestResult.data ?? []).map(mapRequest))
        setMessages((messageResult.data ?? []).map(mapMessage))
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to load Lamsa data.')
    } finally {
      setLoading(false)
    }
  }, [loadStaffProfile])

  useEffect(() => {
    if (!supabase) return
    void loadSupabase()
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      void loadStaffProfile(nextSession).then(() => loadSupabase())
    })
    return () => listener.subscription.unsubscribe()
  }, [loadStaffProfile, loadSupabase])

  useEffect(() => {
    if (!supabase || !staffProfile || !branch.id) return
    const refresh = () => void loadSupabase()
    const channel = supabase
      .channel(`staff-branch-${branch.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `branch_id=eq.${branch.id}` }, refresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'service_requests', filter: `branch_id=eq.${branch.id}` }, refresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'table_sessions', filter: `branch_id=eq.${branch.id}` }, refresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_messages', filter: `branch_id=eq.${branch.id}` }, refresh)
      .subscribe()
    return () => { void supabase.removeChannel(channel) }
  }, [branch.id, loadSupabase, staffProfile])

  const signInStaff = useCallback(async (email: string, password: string) => {
    if (!supabase) {
      setStaffProfile({ id: 'staff-demo', restaurantId: restaurant.id, branchId: branch.id, displayName: 'Demo Manager', role: 'manager' })
      return
    }
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password })
    if (signInError) throw signInError
  }, [branch.id, restaurant.id])

  const signOutStaff = useCallback(async () => {
    if (!supabase) {
      setStaffProfile(null)
      return
    }
    await supabase.auth.signOut()
  }, [])

  const ensureGuestSession = useCallback(async (tableNumber: number, guestName: string): Promise<GuestSessionResult> => {
    if (!supabase) {
      const table = tables.find((t) => t.number === tableNumber)
      if (!table) throw new Error('Table not found.')
      const result = {
        sessionId: table.sessionId ?? `session-${tableNumber}`,
        guestId: `guest-${Date.now()}`,
        guestName,
        tableId: table.id,
        tableNumber,
      }
      if (!table.sessionId) {
        const next = tables.map((t) => t.id === table.id ? { ...t, sessionId: result.sessionId, status: 'browsing' as const, guests: 1 } : t)
        setTables(next)
        persistDemo({ tables: next })
      }
      setGuestSession(result)
      return result
    }

    let current = (await supabase.auth.getSession()).data.session
    if (!current) {
      const anonymous = await supabase.auth.signInAnonymously()
      if (anonymous.error) throw anonymous.error
      current = anonymous.data.session
      setSession(current)
    }
    const { data, error: joinError } = await supabase.rpc('join_table_session', {
      p_restaurant_slug: restaurant.slug,
      p_branch_slug: branch.slug,
      p_table_number: tableNumber,
      p_guest_name: guestName,
    })
    if (joinError) throw joinError
    const row = Array.isArray(data) ? data[0] : data
    const result: GuestSessionResult = {
      sessionId: row.session_id,
      guestId: row.guest_id,
      guestName: row.guest_name,
      tableId: row.table_id,
      tableNumber: row.table_number,
    }
    setGuestSession(result)

    const [cartResult, messageResult] = await Promise.all([
      supabase.from('cart_live_view').select('*').eq('session_id', result.sessionId).order('created_at'),
      supabase.from('chat_messages').select('*').eq('session_id', result.sessionId).order('created_at'),
    ])
    if (!cartResult.error) setCartItems((cartResult.data ?? []).map((row: any) => ({
      id: row.id,
      sessionId: row.session_id,
      guestName: row.guest_name,
      menuItemId: row.menu_item_id,
      name: row.item_name,
      unitPrice: moneyNum(row.unit_price),
      quantity: row.quantity,
    })))
    if (!messageResult.error) setMessages((messageResult.data ?? []).map(mapMessage))
    return result
  }, [branch.slug, persistDemo, restaurant.slug, tables])

  useEffect(() => {
    if (!supabase || !guestSession) return
    const refreshGuest = async () => {
      const [cartResult, messageResult, requestResult, orderResult] = await Promise.all([
        supabase.from('cart_live_view').select('*').eq('session_id', guestSession.sessionId).order('created_at'),
        supabase.from('chat_messages').select('*').eq('session_id', guestSession.sessionId).order('created_at'),
        supabase.from('service_request_live_view').select('*').eq('session_id', guestSession.sessionId).order('created_at'),
        supabase.from('orders').select('*, order_items(*)').eq('session_id', guestSession.sessionId).order('created_at', { ascending: false }),
      ])
      if (!cartResult.error) setCartItems((cartResult.data ?? []).map((row: any) => ({
        id: row.id, sessionId: row.session_id, guestName: row.guest_name, menuItemId: row.menu_item_id,
        name: row.item_name, unitPrice: moneyNum(row.unit_price), quantity: row.quantity,
      })))
      if (!messageResult.error) setMessages((messageResult.data ?? []).map(mapMessage))
      if (!requestResult.error) setRequests((requestResult.data ?? []).map(mapRequest))
      if (!orderResult.error) setOrders((orderResult.data ?? []).map((row: any) => ({
        id: row.id, restaurantId: row.restaurant_id, branchId: row.branch_id, tableId: row.table_id,
        tableNumber: row.table_number, sessionId: row.session_id, status: row.status, createdAt: row.created_at,
        acceptedAt: row.accepted_at ?? undefined, preparingAt: row.preparing_at ?? undefined,
        readyAt: row.ready_at ?? undefined, servedAt: row.served_at ?? undefined, notes: row.notes ?? undefined,
        items: (row.order_items ?? []).map((item: any) => ({ id: item.id, orderId: item.order_id, guestName: item.guest_name, name: item.item_name, quantity: item.quantity, unitPrice: moneyNum(item.unit_price) })),
      })))
    }
    const channel = supabase
      .channel(`guest-session-${guestSession.sessionId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'cart_items', filter: `session_id=eq.${guestSession.sessionId}` }, () => void refreshGuest())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `session_id=eq.${guestSession.sessionId}` }, () => void refreshGuest())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'service_requests', filter: `session_id=eq.${guestSession.sessionId}` }, () => void refreshGuest())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_messages', filter: `session_id=eq.${guestSession.sessionId}` }, () => void refreshGuest())
      .subscribe()
    return () => { void supabase.removeChannel(channel) }
  }, [guestSession])

  const addToCart = useCallback(async (item: MenuItem, guestName: string) => {
    if (!guestSession) throw new Error('Join the table before adding items.')
    if (!supabase) {
      const existing = cartItems.find((row) => row.sessionId === guestSession.sessionId && row.guestName === guestName && row.menuItemId === item.id)
      const next = existing
        ? cartItems.map((row) => row.id === existing.id ? { ...row, quantity: row.quantity + 1 } : row)
        : [...cartItems, { id: `cart-${Date.now()}`, sessionId: guestSession.sessionId, guestName, menuItemId: item.id, name: item.name, unitPrice: item.price, quantity: 1 }]
      setCartItems(next)
      persistDemo({ cartItems: next })
      return
    }
    const { error: cartError } = await supabase.rpc('add_guest_cart_item', { p_session_id: guestSession.sessionId, p_menu_item_id: item.id, p_quantity: 1 })
    if (cartError) throw cartError
  }, [cartItems, guestSession, persistDemo])

  const changeCartQty = useCallback(async (cartId: string, quantity: number) => {
    if (!supabase) {
      const next = quantity <= 0 ? cartItems.filter((row) => row.id !== cartId) : cartItems.map((row) => row.id === cartId ? { ...row, quantity } : row)
      setCartItems(next)
      persistDemo({ cartItems: next })
      return
    }
    if (quantity <= 0) {
      const { error: deleteError } = await supabase.from('cart_items').delete().eq('id', cartId)
      if (deleteError) throw deleteError
    } else {
      const { error: updateError } = await supabase.from('cart_items').update({ quantity }).eq('id', cartId)
      if (updateError) throw updateError
    }
  }, [cartItems, persistDemo])

  const submitOrder = useCallback(async () => {
    if (!guestSession) throw new Error('No active table session.')
    if (!supabase) {
      const sessionCart = cartItems.filter((row) => row.sessionId === guestSession.sessionId)
      if (!sessionCart.length) throw new Error('The shared order is empty.')
      const id = `order-${Date.now()}`
      const order: Order = {
        id, restaurantId: restaurant.id, branchId: branch.id, tableId: guestSession.tableId, tableNumber: guestSession.tableNumber,
        sessionId: guestSession.sessionId, status: 'new', createdAt: new Date().toISOString(),
        items: sessionCart.map((row, index) => ({ id: `${id}-${index}`, orderId: id, guestName: row.guestName, name: row.name, quantity: row.quantity, unitPrice: row.unitPrice })),
      }
      const nextOrders = [order, ...orders]
      const nextCart = cartItems.filter((row) => row.sessionId !== guestSession.sessionId)
      const nextTables = tables.map((t) => t.id === guestSession.tableId ? { ...t, status: 'new_order' as const, elapsedSeconds: 0 } : t)
      setOrders(nextOrders); setCartItems(nextCart); setTables(nextTables)
      persistDemo({ orders: nextOrders, cartItems: nextCart, tables: nextTables })
      return
    }
    const { error: submitError } = await supabase.rpc('submit_table_order', { p_session_id: guestSession.sessionId })
    if (submitError) throw submitError
  }, [branch.id, cartItems, guestSession, orders, persistDemo, restaurant.id, tables])

  const updateOrderStatus = useCallback(async (orderId: string, status: OrderStatus) => {
    const stampField = status === 'confirmed' ? 'accepted_at' : status === 'preparing' ? 'preparing_at' : status === 'ready' ? 'ready_at' : status === 'served' ? 'served_at' : null
    if (!supabase) {
      const next = orders.map((order) => order.id === orderId ? { ...order, status } : order)
      setOrders(next)
      const target = next.find((o) => o.id === orderId)
      if (target) {
        const tableStatus = status === 'preparing' || status === 'confirmed' ? 'preparing' : status === 'ready' ? 'ready' : status === 'served' ? 'browsing' : 'new_order'
        const nextTables = tables.map((t) => t.id === target.tableId ? { ...t, status: tableStatus as DiningTable['status'], elapsedSeconds: 0 } : t)
        setTables(nextTables)
        persistDemo({ orders: next, tables: nextTables })
      }
      return
    }
    const patch: Record<string, unknown> = { status }
    if (stampField) patch[stampField] = new Date().toISOString()
    const { error: statusError } = await supabase.from('orders').update(patch).eq('id', orderId)
    if (statusError) throw statusError
  }, [orders, persistDemo, tables])

  const createServiceRequest = useCallback(async (type: ServiceRequest['type'], message?: string) => {
    if (!guestSession) throw new Error('No active table session.')
    if (!supabase) {
      const request: ServiceRequest = { id: `req-${Date.now()}`, sessionId: guestSession.sessionId, tableId: guestSession.tableId, tableNumber: guestSession.tableNumber, type, status: 'new', createdAt: new Date().toISOString(), message }
      const next = [request, ...requests]
      setRequests(next); persistDemo({ requests: next })
      return
    }
    const { error: requestError } = await supabase.from('service_requests').insert({
      restaurant_id: restaurant.id, branch_id: branch.id, session_id: guestSession.sessionId, table_id: guestSession.tableId,
      request_type: type, message: message ?? null,
    })
    if (requestError) throw requestError
  }, [branch.id, guestSession, persistDemo, requests, restaurant.id])

  const completeRequest = useCallback(async (requestId: string) => {
    if (!supabase) {
      const next = requests.map((row) => row.id === requestId ? { ...row, status: 'completed' as const } : row)
      setRequests(next); persistDemo({ requests: next }); return
    }
    const { error: requestError } = await supabase.from('service_requests').update({ status: 'completed', completed_at: new Date().toISOString() }).eq('id', requestId)
    if (requestError) throw requestError
  }, [persistDemo, requests])

  const sendMessage = useCallback(async (body: string, senderName?: string, sessionId?: string) => {
    const activeSessionId = sessionId ?? guestSession?.sessionId ?? messages.find((m) => m.sessionId)?.sessionId
    if (!activeSessionId) throw new Error('No table conversation selected.')
    const isStaff = Boolean(staffProfile)
    const message: ChatMessage = {
      id: `msg-${Date.now()}`, sessionId: activeSessionId, senderType: isStaff ? 'staff' : 'guest',
      senderName: isStaff ? `${restaurant.name} Team` : (senderName || guestSession?.guestName || 'Guest'), body, createdAt: new Date().toISOString(),
    }
    if (!supabase) {
      const next = [...messages, message]
      setMessages(next); persistDemo({ messages: next }); return
    }
    const targetTable = tables.find((t) => t.sessionId === activeSessionId)
    const { error: messageError } = await supabase.from('chat_messages').insert({
      restaurant_id: restaurant.id,
      branch_id: branch.id,
      session_id: activeSessionId,
      table_id: targetTable?.id ?? guestSession?.tableId,
      sender_type: message.senderType,
      sender_name: message.senderName,
      body,
    })
    if (messageError) throw messageError
  }, [branch.id, guestSession, messages, persistDemo, restaurant.id, restaurant.name, staffProfile, tables])

  const updateRestaurant = useCallback(async (patch: Partial<Restaurant>) => {
    if (!supabase) {
      const next = { ...restaurant, ...patch }
      setRestaurant(next); persistDemo({ restaurant: next }); return
    }
    const dbPatch: Record<string, unknown> = {}
    if (patch.name !== undefined) dbPatch.name = patch.name
    if (patch.tagline !== undefined) dbPatch.tagline = patch.tagline
    if (patch.description !== undefined) dbPatch.description = patch.description
    if (patch.logoUrl !== undefined) dbPatch.logo_url = patch.logoUrl
    if (patch.coverUrl !== undefined) dbPatch.cover_url = patch.coverUrl
    if (patch.primaryColor !== undefined) dbPatch.primary_color = patch.primaryColor
    if (patch.accentColor !== undefined) dbPatch.accent_color = patch.accentColor
    if (patch.orderingEnabled !== undefined) dbPatch.ordering_enabled = patch.orderingEnabled
    const { error: updateError } = await supabase.from('restaurants').update(dbPatch).eq('id', restaurant.id)
    if (updateError) throw updateError
    setRestaurant((prev) => ({ ...prev, ...patch }))
  }, [persistDemo, restaurant])

  const updateMenuItem = useCallback(async (id: string, patch: Partial<MenuItem>) => {
    if (!supabase) {
      const next = menuItems.map((item) => item.id === id ? { ...item, ...patch } : item)
      setMenuItems(next); persistDemo({ menuItems: next }); return
    }
    const dbPatch: Record<string, unknown> = {}
    if (patch.categoryId !== undefined) dbPatch.category_id = patch.categoryId
    if (patch.name !== undefined) dbPatch.name = patch.name
    if (patch.description !== undefined) dbPatch.description = patch.description
    if (patch.price !== undefined) dbPatch.price = patch.price
    if (patch.imageUrl !== undefined) dbPatch.image_url = patch.imageUrl
    if (patch.featured !== undefined) dbPatch.featured = patch.featured
    if (patch.available !== undefined) dbPatch.available = patch.available
    if (patch.sortOrder !== undefined) dbPatch.sort_order = patch.sortOrder
    const { error: itemError } = await supabase.from('menu_items').update(dbPatch).eq('id', id)
    if (itemError) throw itemError
    setMenuItems((prev) => prev.map((item) => item.id === id ? { ...item, ...patch } : item))
  }, [menuItems, persistDemo])

  const addMenuItem = useCallback(async (categoryId: string) => {
    const item: MenuItem = {
      id: `item-${Date.now()}`, restaurantId: restaurant.id, categoryId, name: 'New Menu Item', description: 'Add a description in Studio.',
      price: 0, imageUrl: `https://picsum.photos/seed/lamsa-${Date.now()}/900/700`, featured: false, available: true, sortOrder: menuItems.length + 1,
    }
    if (!supabase) {
      const next = [...menuItems, item]
      setMenuItems(next); persistDemo({ menuItems: next }); return item
    }
    const { data, error: insertError } = await supabase.from('menu_items').insert({
      restaurant_id: restaurant.id, category_id: categoryId, name: item.name, description: item.description, price: item.price,
      image_url: item.imageUrl, featured: false, available: true, sort_order: item.sortOrder,
    }).select('*').single()
    if (insertError) throw insertError
    const mapped = mapMenuItem(data)
    setMenuItems((prev) => [...prev, mapped])
    return mapped
  }, [menuItems, persistDemo, restaurant.id])

  const openTable = useCallback(async (tableId: string) => {
    if (!supabase) {
      const next = tables.map((t) => t.id === tableId ? { ...t, status: 'browsing' as const, sessionId: `session-${Date.now()}`, guests: 0 } : t)
      setTables(next); persistDemo({ tables: next }); return
    }
    const { error: openError } = await supabase.rpc('staff_open_table', { p_table_id: tableId })
    if (openError) throw openError
  }, [persistDemo, tables])

  const closeTable = useCallback(async (tableId: string) => {
    if (!supabase) {
      const next = tables.map((t) => t.id === tableId ? { ...t, status: 'empty' as const, sessionId: undefined, guests: 0, elapsedSeconds: 0 } : t)
      setTables(next); persistDemo({ tables: next }); return
    }
    const { error: closeError } = await supabase.rpc('staff_close_table', { p_table_id: tableId })
    if (closeError) throw closeError
  }, [persistDemo, tables])

  const value: LamsaContextValue = {
    backendMode: isSupabaseConfigured ? 'supabase' : 'demo', loading, error, restaurant, branch, categories, menuItems, tables, orders,
    cartItems, requests, messages, session, staffProfile, guestSession, signInStaff, signOutStaff, ensureGuestSession,
    addToCart, changeCartQty, submitOrder, updateOrderStatus, createServiceRequest, completeRequest, sendMessage,
    updateRestaurant, updateMenuItem, addMenuItem, openTable, closeTable, refresh: loadSupabase,
  }

  return <LamsaContext.Provider value={value}>{children}</LamsaContext.Provider>
}

export function useLamsa() {
  const value = useContext(LamsaContext)
  if (!value) throw new Error('useLamsa must be used within LamsaProvider')
  return value
}
